import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { hubAPI } from '../../../services/hubApi';
import { Hub } from '../../../types/hub';
import { PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton';
import ErrorState from '../../../components/ui/ErrorState';
import EmptyState from '../../../components/ui/EmptyState';
import { useToast } from '../../../hooks/useToast';
import ToastContainer from '../../../components/ui/ToastContainer';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { groupingHref } from '../../../branding/grouping';

export default function HubList() {
  const navigate = useNavigate();
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [filteredHubs, setFilteredHubs] = useState<Hub[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { toasts, success, error: showError, removeToast } = useToast();

  const fetchHubs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await hubAPI.fetchHubs({ includeDrafts: true });
      setHubs(data);
      setFilteredHubs(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load hubs'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHubs();
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredHubs(hubs);
      return;
    }
    const q = searchQuery.toLowerCase();
    setFilteredHubs(
      hubs.filter(
        (t) =>
          t.title.toLowerCase().includes(q) ||
          t.slug.toLowerCase().includes(q) ||
          (t.description || '').toLowerCase().includes(q)
      )
    );
  }, [searchQuery, hubs]);

  const [featuringId, setFeaturingId] = useState<string | null>(null);

  const handleFeatured = async (hub: Hub) => {
    try {
      setFeaturingId(hub.id);
      const updated = await hubAPI.updateHub(hub.id, { featured: !hub.featured });
      setHubs((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      success(updated.featured ? 'Hub will appear on the home' : 'Hub removed from the home');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update featured hubs');
    } finally {
      setFeaturingId(null);
    }
  };

  const handleDelete = async (hub: Hub) => {
    if (!confirm(`Delete hub "${hub.title}"? This cannot be undone.`)) return;
    try {
      setDeletingId(hub.id);
      await hubAPI.deleteHub(hub.id);
      setHubs((prev) => prev.filter((t) => t.id !== hub.id));
      success('Hub deleted');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete hub');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSkeleton lines={8} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorState error={error} title="Failed to load toolkits" onRetry={fetchHubs} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <ToastContainer toasts={toasts} onClose={removeToast} />

      <Breadcrumb
        items={[
          { label: 'Home', to: '/' },
          { label: 'Toolkits' },
        ]}
      />

      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Toolkits</h1>
          <p className="mt-1 text-sm text-gray-600">
            Create hubs, add content sections and resource groups, and choose which published hubs appear on the home. The home shows up to three.
          </p>
        </div>
        <Button
          onClick={() => navigate('/admin/hubs/new')}
          className="flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          Create Hub
        </Button>
      </div>

      <div className="mb-6">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder="Search hubs by title, description, or slug..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {filteredHubs.length === 0 ? (
        <EmptyState
          title={searchQuery ? 'No hubs found' : 'No hubs yet'}
          description={
            searchQuery
              ? 'Try adjusting your search query'
              : 'Get started by creating your first hub'
          }
          actionLabel={!searchQuery ? 'Create Hub' : undefined}
          onAction={!searchQuery ? () => navigate('/admin/hubs/new') : undefined}
        />
      ) : (
        <div className="space-y-4">
          {filteredHubs.map((hub) => (
            <Card key={hub.id} className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-gray-900">{hub.title}</h3>
                    {hub.status === 'DRAFT' && (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                        Draft
                      </span>
                    )}
                    {hub.featured && (
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                        Featured
                      </span>
                    )}
                  </div>
                  {hub.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{hub.description}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                    <span>Slug: {hub.slug}</span>
                    <span>•</span>
                    <span>{hub.resources?.length ?? 0} resources</span>
                    <span>•</span>
                    <span>{hub.sections?.length ?? 0} sections</span>
                    {(hub.resources || []).some((r) => r.isHighlighted) && (
                      <>
                        <span>•</span>
                        <span>
                          {(hub.resources || []).filter((r) => r.isHighlighted).length}{' '}
                          highlighted
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <label className="inline-flex items-center gap-2 min-h-[44px] text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      checked={hub.featured === true}
                      disabled={featuringId === hub.id}
                      onChange={() => handleFeatured(hub)}
                    />
                    Feature on home
                  </label>
                  {hub.status === 'PUBLISHED' ? (
                    <Link
                      to={groupingHref(hub.slug)}
                      className="text-sm text-primary hover:underline px-2"
                    >
                      View
                    </Link>
                  ) : (
                    <span className="text-sm text-gray-400 px-2">Not public</span>
                  )}
                  <Button
                    variant="secondary"
                    className="!px-3 !py-2"
                    onClick={() => navigate(`/admin/hubs/${hub.id}/edit`)}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    className="!px-3 !py-2"
                    disabled={deletingId === hub.id}
                    onClick={() => handleDelete(hub)}
                  >
                    <TrashIcon className="h-4 w-4 text-error" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
